import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { GetHydratedCallRecordRequest } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import { IncludesAnalysis, useRecentCallsQuery } from '@frontend/api-call-intel';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { useDemoPanel } from '@frontend/demo-panels';
import { ErrorBoundary, MainBoundary } from '@frontend/error-boundary';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { mergeHydratedCallFields } from '../../hooks/use-call-insight-controller';
import { useCallInsightPanelShallowStore } from '../../hooks/use-call-insight-panel-store';
import { useScreens } from '../../hooks/use-screens';
import { CallInsightPanelSource, ScreenBaseProps, ScreenType } from '../../types';
import { CallInsightHeader } from '../call-insight-header';
import { CallInsightPanelLoader } from '../call-insight-panel-loader';
import { Screens } from '../screens/screens-map';
import { useCallInsightDemoPanelInterceptor } from './use-call-insight-demo-panel-interceptor';

/**
 * Default source for demo call insight panels when not explicitly specified.
 * It is listed in `NON_ACTIVE_SOURCES`, so opening it never changes the
 * global `activeSource` and the real call insight panel behind the demo drawer keeps its own context.
 */
const DEFAULT_DEMO_SOURCE: CallInsightPanelSource = 'callInsightTrialTeaser';

const RECENT_CALLS_ANALYSIS = [
  IncludesAnalysis.CALL_CATEGORY,
  IncludesAnalysis.CALL_SUMMARY,
  IncludesAnalysis.CALL_SENTIMENT,
  IncludesAnalysis.FOLLOWUP_TASKS,
  IncludesAnalysis.CALL_APPOINTMENT_TYPE,
  IncludesAnalysis.CALL_APPOINTMENTS,
  IncludesAnalysis.CALL_SERVICE_QUALITY,
  IncludesAnalysis.CALL_SCHEDULING_OUTCOME,
];

export interface DemoCallInsightPanelComponentProps {
  demoLocationId: string;
  source?: CallInsightPanelSource;
}

export const DemoCallInsightPanelComponent: FC<DemoCallInsightPanelComponentProps> = ({
  demoLocationId,
  source = DEFAULT_DEMO_SOURCE,
}) => {
  const { t } = useTranslation('call-insight-panel');

  const { isDemoDataReady, demoPerson } = useCallInsightDemoPanelInterceptor({
    enabled: true,
    locationId: demoLocationId,
  });

  const { contexts, openContext, closeContext } = useCallInsightPanelShallowStore(
    'contexts',
    'openContext',
    'closeContext'
  );

  const demoPanelContext = useDemoPanel();
  const openedCallIdRef = useRef<string | null>(null);

  const ctx = contexts[source];
  const currentScreenView = ctx?.callListOptions.currentScreenView;
  const { screenType, data: payload, setScreen, reset } = useScreens(currentScreenView ?? ScreenType.CALL_INSIGHTS);

  const isInsideDemoPanel = demoPanelContext?.isInsideDemoPanel ?? false;
  const showBackButton = isInsideDemoPanel && (demoPanelContext?.isTopOfStack ?? false);

  const Screen =
    (Screens[screenType] as React.ComponentType<ScreenBaseProps<typeof screenType>>) ||
    Screens[ScreenType.CALL_INSIGHTS];

  // The interceptors rewrite this request for the demo person, so the response is based on the location's vertical.
  const {
    data: recentCall,
    isLoading: isRecentCallLoading,
    isError: isRecentCallError,
  } = useRecentCallsQuery({
    request: {
      locations: [demoLocationId],
      personId: demoPerson.personId,
      includesAnalysis: RECENT_CALLS_ANALYSIS,
      limit: 1,
    },
    options: {
      enabled: isDemoDataReady,
      cacheTime: 0,
      select: (response) => response.calls?.[0]?.call,
    },
  });

  const callIntelData = useMemo<HydratedCallRecord['callIntelInsights'] | undefined>(() => {
    if (!recentCall) return undefined;
    return {
      ...recentCall,
      locationId: recentCall.locationId || demoLocationId,
      person: recentCall.person ?? {
        id: demoPerson.personId,
        firstName: demoPerson.firstName,
        lastName: demoPerson.lastName,
      },
    };
  }, [recentCall, demoLocationId, demoPerson]);

  const channelId = callIntelData?.id;

  const { data: hydratedCallResponse, isError: isHydratedCallError } = useScopedQuery({
    queryKey: ['hydrated-call-record', source, demoLocationId, channelId],
    queryFn: () => {
      const request: GetHydratedCallRecordRequest = {
        locationId: demoLocationId,
        channelId,
        includeCallRecording: true,
        hydrationFlags: {
          includeCallRecording: true,
          includeCallIntelligence: false,
          includeUserInfo: !callIntelData?.officeUser,
          includePersonInfo: !callIntelData?.person,
          includePhoneTreeData: true,
          includeDownloadCallRecordingToken: true,
        },
      };
      return PhoneCallsApi.getCallRecord({
        ...request,
        isDemo: true,
        demoMetadata: {
          personId: demoPerson.personId,
          callRecord: JSON.stringify(callIntelData),
        } as any, // Some of these values are not in contract but will be used by demo interceptor,
      });
    },
    enabled: isDemoDataReady && !!channelId,
    cacheTime: 0,
  });

  const handleClose = useCallback(() => {
    reset();
    closeContext({ source });
    demoPanelContext?.close();
  }, [reset, closeContext, demoPanelContext, source]);

  // Seed the isolated demo context once the hydrated demo call is available. Never goes through the controller's
  // `open`, which would try to open the global panel.
  useEffect(() => {
    const record = hydratedCallResponse?.record;
    if (!record || !channelId || openedCallIdRef.current === channelId) return;

    openedCallIdRef.current = channelId;
    openContext({
      call: mergeHydratedCallFields(record, callIntelData),
      opts: {
        isPLGDemo: true,
        isDemoAccount: true,
        defaultAccordion: 'callIntel',
      },
      source,
    });
  }, [hydratedCallResponse?.record, channelId, callIntelData, openContext, source]);

  // Clear only the demo context when the demo panel goes away, the real panel's context is left untouched.
  useEffect(() => {
    return () => {
      closeContext({ source });
    };
  }, [closeContext, source]);

  useEffect(() => {
    reset();
  }, [ctx?.selectedCall?.id, reset]);

  const isEmpty = isDemoDataReady && !isRecentCallLoading && !isRecentCallError && !recentCall;
  if (isRecentCallError || isHydratedCallError || isEmpty) {
    return (
      <div css={styles.errorWrapper}>
        <CallInsightHeader onClose={handleClose} showBackButton={showBackButton} showCloseButton={false} />
        <Text color='subdued' css={{ padding: theme.spacing(2) }} data-testid='demo-call-insight-error'>
          {t('Unable to load the sample call right now. Please try again later.')}
        </Text>
      </div>
    );
  }

  if (!isDemoDataReady || !ctx?.selectedCall) {
    return (
      <div css={{ padding: theme.spacing(2) }}>
        <CallInsightPanelLoader onClose={handleClose} />
      </div>
    );
  }

  return (
    <ErrorBoundary fallback={MainBoundary} tier='feature' feature='demo-call-insight-panel' team='fe-analytics'>
      <Screen
        source={source}
        key={ctx.selectedCall.id}
        setScreen={setScreen}
        data={payload}
        onClose={handleClose}
        showBackButton={showBackButton}
        showCloseButton={false}
      />
    </ErrorBoundary>
  );
};

const styles = {
  errorWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
  },
};
